import { Button, KpiTile, SegmentedControl, StatusPill } from "@flexfreight/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { api, ApiError } from "../api/client";
import { useConsoleMe } from "../auth/context";

type Money = { cents: number; currency: "USD" };
type Metric<T> = { value: T; previous: T; deltaPct: number | null };
export type DashboardData = {
  range: Range;
  from: string;
  to: string;
  showsCost: boolean;
  kpis: {
    quotes: Metric<number>;
    bookings: Metric<number>;
    revenue: Metric<Money>;
    margin?: Metric<Money>;
    newCustomers: Metric<number>;
    activeShipments: number;
    exceptions: number;
    signupsPending: number;
    openLeads: number;
    openTickets: number | null;
  };
  trend: { day: string; quotes: number; bookings: number; revenue: Money; margin?: Money }[];
  carriers: { carrier: string; shipments: number; delivered: number; onTimePct: number | null; exceptions: number; revenue: Money }[];
  topCustomers: { id: string; name: string; shipments: number; quotes: number; revenue: Money }[];
  issues: { id: string; orgId: string; orgName: string; carrier: string; bolNumber: string | null; description: string | null; at: string | null }[];
};
type Range = "today" | "7d" | "30d" | "90d";
const RANGES: { value: Range; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];
const CONTEXT: Record<Range, string> = { today: "vs yesterday", "7d": "vs prior 7 days", "30d": "vs prior 30 days", "90d": "vs prior 90 days" };
const CARRIER: Record<string, string> = { xpo: "XPO", odfl: "Old Dominion", estes: "Estes", saia: "Saia", rl: "R+L", abf: "ABF", fedex_freight: "FedEx Freight", tforce: "TForce", amfast: "AmFast", eagleload: "EagleLoad", wwex: "Worldwide Express", mock: "Mock" };
const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const dayFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const timeFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Overview > Dashboard: the day's command center from live data. Every number opens its records. */
export function DashboardPage() {
  const me = useConsoleMe();
  const [params, setParams] = useSearchParams();
  const raw = params.get("range") as Range | null;
  const range: Range = raw && RANGES.some((r) => r.value === raw) ? raw : "7d";
  const q = useQuery({
    queryKey: ["console", "dashboard", range],
    queryFn: () => api<DashboardData>(`/v1/console/dashboard?range=${range}`),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const d = q.data;
  const can = (c: string) => me.capabilities.includes(c as never);
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Dashboard</h1>
          <p className="ffa-head__sub">Live from FlexFreight&apos;s own quotes, bookings and shipments. Updates every minute.</p>
        </div>
        <div className="ffa-head__actions">
          <SegmentedControl label="Date range" options={RANGES} value={range} onChange={(v) => setParams(v === "7d" ? {} : { range: v }, { replace: true })} />
          <Link className="ffx-btn" to="/wall">Wallboard</Link>
        </div>
      </header>
      {q.isError ? (
        <div className="ffa-notice" role="alert">
          <span>Couldn&apos;t load the dashboard. {message(q.error)}</span>
          <Button size="sm" onClick={() => void q.refetch()}>Try again</Button>
        </div>
      ) : null}
      {!d ? (
        <p className="ffa-muted" aria-busy="true">Loading the dashboard...</p>
      ) : (
        <>
          <div className="ffa-kpis ffa-dash__kpis" aria-busy={q.isFetching}>
            <KpiLink to="/quotes" show={can("quotes:read")}>
              <KpiTile label="Quotes" value={String(d.kpis.quotes.value)} {...deltaOf(d.kpis.quotes)} deltaContext={CONTEXT[range]} trend={d.trend.map((t) => t.quotes)} />
            </KpiLink>
            <KpiLink to="/shipments?bucket=all" show={can("shipments:read")}>
              <KpiTile label="Bookings" value={String(d.kpis.bookings.value)} {...deltaOf(d.kpis.bookings)} deltaContext={CONTEXT[range]} trend={d.trend.map((t) => t.bookings)} chart="bars" />
            </KpiLink>
            <KpiLink to="/shipments?bucket=all" show={can("shipments:read")}>
              <KpiTile label="Revenue" value={usd0.format(d.kpis.revenue.value.cents / 100)} {...deltaOf(d.kpis.revenue, true)} deltaContext={CONTEXT[range]} trend={d.trend.map((t) => t.revenue.cents)} />
            </KpiLink>
            {d.kpis.margin ? (
              <KpiLink to="/shipments?bucket=all" show={can("shipments:read")}>
                <KpiTile
                  label={`Gross margin${d.kpis.revenue.value.cents ? ` · ${Math.round((d.kpis.margin.value.cents / d.kpis.revenue.value.cents) * 1000) / 10}%` : ""}`}
                  value={usd0.format(d.kpis.margin.value.cents / 100)}
                  {...deltaOf(d.kpis.margin, true)}
                  deltaContext={CONTEXT[range]}
                  trend={d.trend.map((t) => t.margin?.cents ?? 0)}
                />
              </KpiLink>
            ) : null}
            <KpiLink to="/signups?status=all" show={can("customers:read")}>
              <KpiTile label="New customers" value={String(d.kpis.newCustomers.value)} {...deltaOf(d.kpis.newCustomers)} deltaContext={CONTEXT[range]} />
            </KpiLink>
          </div>

          <div className="ffa-dash__grid">
            <section className="ffx-card ffa-card ffa-dash__trend" aria-labelledby="trend-h">
              <div className="ffa-card__headrow">
                <h2 id="trend-h" className="ffa-card__title">Sales trend</h2>
                <span className="ffa-muted ffa-small">Revenue per day{d.showsCost ? ", margin in the darker band" : ""}</span>
              </div>
              <TrendChart d={d} />
            </section>

            <section className="ffx-card ffa-card" aria-labelledby="now-h">
              <h2 id="now-h" className="ffa-card__title">Right now</h2>
              <ul className="ffa-queue">
                <QueueItem to="/shipments?bucket=active" label="Shipments in motion" value={d.kpis.activeShipments} show={can("shipments:read")} />
                <QueueItem to="/shipments?bucket=exception" label="Exceptions" value={d.kpis.exceptions} tone={d.kpis.exceptions ? "danger" : undefined} show={can("shipments:read")} />
                <QueueItem to="/signups" label="Signups to review" value={d.kpis.signupsPending} tone={d.kpis.signupsPending ? "warning" : undefined} show={can("customers:read")} />
                <QueueItem to="/leads" label="Open leads" value={d.kpis.openLeads} show={can("leads:read")} />
                {d.kpis.openTickets !== null ? <QueueItem to="/tickets" label="Open tickets" value={d.kpis.openTickets} show={can("tickets:read")} /> : null}
              </ul>
            </section>

            <section className="ffx-card ffa-card" aria-labelledby="issues-h">
              <h2 id="issues-h" className="ffa-card__title">Recent issues</h2>
              {d.issues.length === 0 ? (
                <p className="ffa-muted ffa-small">No open exceptions.</p>
              ) : (
                <ul className="ffa-issues">
                  {d.issues.map((i) => (
                    <li key={i.id}>
                      <Link className="ffa-rowlink ffa-cell-strong" to={`/shipments?bucket=exception&q=${encodeURIComponent(i.bolNumber ?? "")}`}>
                        {i.bolNumber ?? "Shipment"} · {CARRIER[i.carrier] ?? i.carrier}
                      </Link>
                      <span className="ffa-small">{i.description ?? "Exception reported"}</span>
                      <span className="ffa-muted ffa-small">
                        {i.orgName}
                        {i.at ? ` · ${timeFmt.format(new Date(i.at))}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ffx-card ffa-card" aria-labelledby="carriers-h">
              <h2 id="carriers-h" className="ffa-card__title">Carrier performance</h2>
              {d.carriers.length === 0 ? (
                <p className="ffa-muted ffa-small">No bookings in this period.</p>
              ) : (
                <table className="ffx-table ffa-minitable">
                  <caption className="ffx-sr">Carrier performance for the period</caption>
                  <thead>
                    <tr>
                      <th scope="col">Carrier</th>
                      <th scope="col" className="ffx-table__num">Loads</th>
                      <th scope="col" className="ffx-table__num">On time</th>
                      <th scope="col" className="ffx-table__num">Exceptions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.carriers.map((c) => (
                      <tr key={c.carrier}>
                        <th scope="row">
                          <Link className="ffa-rowlink" to={`/shipments?bucket=all&carrier=${c.carrier}`}>{CARRIER[c.carrier] ?? c.carrier}</Link>
                        </th>
                        <td className="ffx-table__num ffx-num">{c.shipments}</td>
                        <td className="ffx-table__num ffx-num">{c.onTimePct === null ? <span className="ffa-muted">-</span> : `${c.onTimePct}%`}</td>
                        <td className="ffx-table__num ffx-num">{c.exceptions ? <StatusPill tone="danger">{c.exceptions}</StatusPill> : 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="ffa-muted ffa-small">On time = delivered by the carrier&apos;s estimated date.</p>
            </section>

            <section className="ffx-card ffa-card" aria-labelledby="cust-h">
              <h2 id="cust-h" className="ffa-card__title">Customer activity</h2>
              {d.topCustomers.length === 0 ? (
                <p className="ffa-muted ffa-small">No customers yet.</p>
              ) : (
                <table className="ffx-table ffa-minitable">
                  <caption className="ffx-sr">Most active customers for the period</caption>
                  <thead>
                    <tr>
                      <th scope="col">Customer</th>
                      <th scope="col" className="ffx-table__num">Quotes</th>
                      <th scope="col" className="ffx-table__num">Loads</th>
                      <th scope="col" className="ffx-table__num">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.topCustomers.map((c) => (
                      <tr key={c.id}>
                        <th scope="row">
                          <Link className="ffa-rowlink ffa-trunc" to={`/customers/${c.id}`} title={c.name}>{c.name}</Link>
                        </th>
                        <td className="ffx-table__num ffx-num">{c.quotes}</td>
                        <td className="ffx-table__num ffx-num">{c.shipments}</td>
                        <td className="ffx-table__num ffx-num">{usd0.format(c.revenue.cents / 100)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </>
      )}
    </section>
  );
}

function deltaOf(m: Metric<number> | Metric<Money>, _money = false): { delta?: number } {
  return m.deltaPct === null ? {} : { delta: m.deltaPct };
}

function KpiLink({ to, show, children }: { to: string; show: boolean; children: React.ReactNode }) {
  return show ? (
    <Link to={to} className="ffa-kpilink">
      {children}
    </Link>
  ) : (
    <div>{children}</div>
  );
}

function QueueItem({ to, label, value, tone, show }: { to: string; label: string; value: number; tone?: "danger" | "warning" | undefined; show: boolean }) {
  const inner = (
    <>
      <span>{label}</span>
      {tone ? <StatusPill tone={tone}>{value}</StatusPill> : <span className="ffx-num ffa-cell-strong">{value}</span>}
    </>
  );
  return <li className="ffa-queue__item">{show ? <Link to={to} className="ffa-queue__link">{inner}</Link> : inner}</li>;
}

/** Revenue bars per day (margin overlaid when allowed), with a text table for screen readers. */
function TrendChart({ d }: { d: DashboardData }) {
  const max = Math.max(1, ...d.trend.map((t) => t.revenue.cents));
  const total = d.trend.reduce((n, t) => n + t.revenue.cents, 0);
  const label = `Revenue per day from ${dayFmt.format(new Date(d.trend[0]?.day ?? d.from))} to ${dayFmt.format(new Date(d.trend.at(-1)?.day ?? d.to))}, ${usd0.format(total / 100)} in total.`;
  const every = Math.ceil(d.trend.length / 8);
  return (
    <figure className="ffa-trend">
      <div className="ffa-trend__plot" role="img" aria-label={label}>
        <div className="ffa-trend__scale" aria-hidden="true">
          <span>{compact.format(max / 100)}</span>
          <span>{compact.format(max / 200)}</span>
          <span>$0</span>
        </div>
        <div className="ffa-trend__bars" style={{ gridTemplateColumns: `repeat(${d.trend.length}, minmax(0, 1fr))` }} aria-hidden="true">
          {d.trend.map((t, i) => (
            <div key={t.day} className="ffa-trend__col" title={`${dayFmt.format(new Date(t.day))}: ${usd0.format(t.revenue.cents / 100)}, ${t.bookings} booking${t.bookings === 1 ? "" : "s"}`}>
              <div className="ffa-trend__bar" style={{ blockSize: `${(t.revenue.cents / max) * 100}%` }}>
                {t.margin && t.revenue.cents > 0 ? <div className="ffa-trend__margin" style={{ blockSize: `${Math.max(0, (t.margin.cents / t.revenue.cents) * 100)}%` }} /> : null}
              </div>
              <span className="ffa-trend__x">{i % every === 0 ? dayFmt.format(new Date(t.day)) : ""}</span>
            </div>
          ))}
        </div>
      </div>
      {total === 0 ? <figcaption className="ffa-muted ffa-small">No bookings in this period yet.</figcaption> : null}
      <table className="ffx-sr">
        <caption>Daily revenue and bookings</caption>
        <thead>
          <tr><th scope="col">Day</th><th scope="col">Revenue</th><th scope="col">Bookings</th><th scope="col">Quotes</th></tr>
        </thead>
        <tbody>
          {d.trend.map((t) => (
            <tr key={t.day}><th scope="row">{dayFmt.format(new Date(t.day))}</th><td>{usd0.format(t.revenue.cents / 100)}</td><td>{t.bookings}</td><td>{t.quotes}</td></tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
